/**
 * viewing-map.js
 */

import React from 'react';
import { Box, Button } from '@mui/material';
import { UserContext } from 'contexts/user/user-context';
import Dialog from 'components/dialog';
import { TrackingProviders } from '../tracking-providers';
import { TrackContext } from '../track-context';
import { TRACK_TYPE } from '../tracking-common';
import { MAP_TYPE } from './map-common';
import { MapContext } from './map-context';
import { fetchTracks, MapBase } from './viewing-map';


const REFRESH_TRACKING_MS = 20 * 1000;
const REFRESH_TRACKING_SUPERVISORS_MS = 5 * 1000;


const LiveMultiMapTitle = (props) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
      <Box sx={{ lineHeight: 1.2 }}>Tracking Deployments</Box>
      <Box sx={{ fontSize: 'sizes.2' }}>Last 4 Hours</Box>
    </Box>
  );
}


const DialogTitle = {
  [TRACK_TYPE.FOLLOW]: 'Exercise Tracking Map',
  [TRACK_TYPE.DEPLOYMENT]: 'Deployment Tracking Map',
  [TRACK_TYPE.MULTI_DEPLOYMENT]: <LiveMultiMapTitle />
}


// full-screen map dialog needs its own map provider.
// since we already have tracks from query we pass it through to this context
// rather than re-querying all over again.
const MapDialog = (props) => {
  const { 
    title,
    onClose,
    ...rest
  } = props;
  const { isDialogOpen } = React.useContext(ViewingMapContext);
  const { deselectMarker, deselectPoint } = React.useContext(MapContext);


  // close marker/point drawer when dialog is opened/closed
  React.useEffect(() => {
    deselectMarker();
    deselectPoint();
  }, [isDialogOpen, deselectMarker, deselectPoint]);


  return (
    <Dialog
      title={title || 'Tracking Map'}
      titleActions={
        <Button
          variant="contained"
          color="primary"
          onClick={() => onClose()}
          children='Close'
        />
      }
      fullScreen
      fullWidth
      showOk={false}
      showCancel={false}
      open={isDialogOpen}
      onClose={() => onClose()}
      sx={{ 
        '& .MuiDialogContent-root': {
          padding: 0,
          paddingTop: `0px !important`
        },
        '& .MuiDialogContentText-root': {
          height: '100%'
        },
        '& .MuiDialogActions-root': {
          display: 'none'
        }
      }}
      {...rest}
      children={<MapBase isDialog />}
    />
  )
}


export const ViewingMapContext = React.createContext();


export const ViewingMapProvider = (props) => {
  return (
    <TrackingProviders ids={['track', 'map', 'drawer']}>
      <ViewingMapProviderNested>
        {props.children}
      </ViewingMapProviderNested>
    </TrackingProviders>
  )
}


const updateRate = (type) => {
  return type === TRACK_TYPE.MULTI_DEPLOYMENT
    ? REFRESH_TRACKING_SUPERVISORS_MS
    : REFRESH_TRACKING_MS;
}


export const ViewingMapProviderNested = (props) => {
  const { children } = props;
  const { user } = React.useContext(UserContext);
  const { setTracks } = React.useContext(TrackContext);
  const { setMapType } = React.useContext(MapContext);
  const refreshTimeoutRef = React.useRef();
  const mapRefCountRef = React.useRef(0);
  const [state, setState] = React.useState(({
    type: null,
    recordId: null,
    trackingId: null,
    mapReferenceCount: 0,
    dialogTitle: null,
    isDialogOpen: false,
    isLoading: false,
  }));


  // cleanup when we have no more references
  React.useEffect(() => {
    if (state.mapReferenceCount === 0) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, [state.mapReferenceCount]);


  // fetch track data
  const updateTracks = React.useCallback(async (type, recordId, trackingId) => {
    if (mapRefCountRef.current === 0) {
      setState(p => ({ ...p, isLoading: true }));
    }
    const tracks = await fetchTracks(type, recordId, trackingId, user);
    setState(p => ({ ...p, isLoading: false }));
    
    if (!tracks) {
      setTracks([]);
    } else {
      setTracks(tracks.map(track => ({ ...track, type: type === TRACK_TYPE.MULTI_DEPLOYMENT ? type : track.type })));
    }

    // check if we should keep refreshing
    if (
      type === TRACK_TYPE.MULTI_DEPLOYMENT
      || (
        tracks
        && tracks.length > 0
        && tracks.find(track => !track.isComplete)
      )
    ) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = setTimeout(() => updateTracks(type, recordId, trackingId), updateRate(type));
    } else {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, [user, setTracks]);


  // sets map params used for viewing drawer map and dialog map
  const setMapParams = React.useCallback((type, recordId, trackingId, tracks) => {
    // refresh interval helper: start if no tracks or at least 1 is incomplete
    const tryStartRefreshInterval = () => {
      if (!tracks || tracks.find(track => !track.isComplete)) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = setTimeout(() => updateTracks(type, recordId, trackingId), updateRate(type));
      }
    }

    // if requesting most recent map we can reuse the existing state data.
    // we only need to try restarting the refresh interval as it may have been stopped.
    if (type !== TRACK_TYPE.MULTI_DEPLOYMENT && type === state.type && recordId === state.recordId && trackingId === state.trackingId) {
      tryStartRefreshInterval();
      return;
    } 
    
    // init defaults
    clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = null;
    setTracks([]);
    setMapType(MAP_TYPE.SATELLITE);

    // initialize tracks
    if (tracks) {
      setTracks(tracks);
    } else {
      updateTracks(type, recordId, trackingId);
    }

    tryStartRefreshInterval();

    // set title
    setState(p => ({
      ...p,
      type,
      recordId,
      trackingId,
      dialogTitle: DialogTitle[type]
    }));
  }, [
    state.type, 
    state.recordId, 
    state.trackingId, 
    setTracks, 
    setMapType,
    updateTracks
  ]);


  // used by MapBase to set whether it has a reference or not. once we have no references we can cleanup.
  const setHasReference = React.useCallback((val) => {
    setState(p => ({
      ...p,
      mapReferenceCount: p.mapReferenceCount + (val ? 1 : -1)
    }));
    mapRefCountRef.current = mapRefCountRef.current + (val ? 1 : -1);
  }, []);


  const setIsDialogOpen = React.useCallback((isDialogOpen) => {
    setState(p => ({
      ...p,
      isDialogOpen
    }));

    window.Intercom('update', {
      custom_launcher_selector:'#intercom-chat-link',
      hide_default_launcher: isDialogOpen,
    });
  }, []);


  const value = React.useMemo(() => ({
    ...state,
    setMapParams,
    setHasReference,
    setIsDialogOpen,
  }), [
    state,
    setMapParams,
    setHasReference,
    setIsDialogOpen
  ]);


  return (<>
    <ViewingMapContext.Provider value={value}>
      {children}
      <MapDialog
        title={state.dialogTitle}
        onClose={() => setIsDialogOpen(false)}
      />
    </ViewingMapContext.Provider>
  </>);
}